import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { NotificationCategory, NotificationType } from '../../services/notifications.service';
import { cn } from '../../lib/utils';

// How notifications look everywhere they are shown: the bell dropdown, the page and the settings

export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  [NotificationCategory.INVENTORY]: 'Inventory',
  [NotificationCategory.SALES]: 'Sales',
  [NotificationCategory.PURCHASE]: 'Purchase',
  [NotificationCategory.MANUFACTURING]: 'Manufacturing',
  [NotificationCategory.FINANCE]: 'Finance',
  [NotificationCategory.SYSTEM]: 'System',
};

export const CATEGORY_TONES: Record<NotificationCategory, string> = {
  [NotificationCategory.INVENTORY]: 'bg-purple-50 text-purple-700 ring-purple-600/20',
  [NotificationCategory.SALES]: 'bg-green-50 text-green-700 ring-green-600/20',
  [NotificationCategory.PURCHASE]: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  [NotificationCategory.MANUFACTURING]: 'bg-orange-50 text-orange-700 ring-orange-600/20',
  [NotificationCategory.FINANCE]: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  [NotificationCategory.SYSTEM]: 'bg-gray-50 text-gray-600 ring-gray-500/20',
};

export const TYPE_LABELS: Record<string, string> = {
  info: 'Information',
  success: 'Success',
  warning: 'Warning',
  error: 'Urgent',
  inventory_reorder: 'Reorder',
};

export const NotificationIcon: React.FC<{ type: NotificationType | string; className?: string }> = ({ type, className }) => {
  const size = cn('h-5 w-5', className);
  switch (type) {
    case 'success':
      return <CheckCircleIcon className={cn(size, 'text-green-500')} />;
    case 'warning':
    case 'inventory_reorder':
      return <ExclamationTriangleIcon className={cn(size, 'text-amber-500')} />;
    case 'error':
      return <XCircleIcon className={cn(size, 'text-red-500')} />;
    default:
      return <InformationCircleIcon className={cn(size, 'text-blue-500')} />;
  }
};

// "5 minutes ago", "Yesterday, 4:10 pm", "03 Oct 2026"
export const formatWhen = (value: string): string => {
  const date = new Date(value);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  const time = date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  if (date >= startOfToday) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (date.getTime() >= startOfToday.getTime() - 24 * 60 * 60 * 1000) return `Yesterday, ${time}`;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// The heading a notification is listed under: Today, Yesterday, Earlier this week, Older
export const dayGroup = (value: string): string => {
  const date = new Date(value);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const day = 24 * 60 * 60 * 1000;
  if (date >= startOfToday) return 'Today';
  if (date.getTime() >= startOfToday.getTime() - day) return 'Yesterday';
  if (date.getTime() >= startOfToday.getTime() - 6 * day) return 'Earlier this week';
  return 'Older';
};
